import { dataCenterFromRegionParam, getGithubConnectUrl, getSelectedDC, setSelectedDC } from '../config'
import { apiClient } from './apiClient'

const INSTALL_RETURN_COOKIE = 'vs_install_return'
const INSTALL_DC_COOKIE = 'vs_install_dc'
const PENDING_INSTALLATION_KEY = 'vs_pending_github_installation_id'
const PENDING_INSTALL_STATE_KEY = 'vs_pending_github_install_state'

/** GitHub App installation ids are numeric. Origin ids look like i_…. */
export function isGitHubInstallationId(id: string | null | undefined): id is string {
  return Boolean(id && /^\d+$/.test(id.trim()))
}

export function isGitHubInstallState(raw: string | null | undefined): raw is string {
  if (!raw || raw.startsWith('http')) return false
  return raw.split('.').length === 3
}

export function rememberPendingGitHubInstallation(installationId: string, state?: string): void {
  if (typeof sessionStorage === 'undefined') return
  if (!isGitHubInstallationId(installationId)) return
  sessionStorage.setItem(PENDING_INSTALLATION_KEY, installationId)
  if (state && isGitHubInstallState(state)) {
    sessionStorage.setItem(PENDING_INSTALL_STATE_KEY, state)
  }
}

export function peekPendingGitHubInstallation(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  return sessionStorage.getItem(PENDING_INSTALLATION_KEY)
}

export function peekPendingGitHubInstallState(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  return sessionStorage.getItem(PENDING_INSTALL_STATE_KEY)
}

export function clearPendingGitHubInstallation(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(PENDING_INSTALLATION_KEY)
  sessionStorage.removeItem(PENDING_INSTALL_STATE_KEY)
}

function setInstallCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; domain=.veilstream.com; path=/; max-age=3600; Secure; SameSite=Lax`
}

function readInstallCookie(name: string): string | null {
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

function normalizeInstallReturnPath(returnPath: string): string {
  const path = returnPath.trim() || '/projects'
  return path.startsWith('/') ? path : `/${path}`
}

/** Remember console return path + data center for GitHub App install callbacks. */
export function primeGitHubAppInstallReturn(returnPath = '/projects'): void {
  if (typeof document === 'undefined') return
  setInstallCookie(INSTALL_RETURN_COOKIE, normalizeInstallReturnPath(returnPath))
  setInstallCookie(INSTALL_DC_COOKIE, getSelectedDC())
}

/** Apply data center from install callback URL, cookies, or session before connect. */
export function applyInstallDataCenter(searchParams: URLSearchParams): void {
  const region = searchParams.get('region')
  if (region) {
    const dc = dataCenterFromRegionParam(region)
    if (dc) {
      setSelectedDC(dc)
      return
    }
  }
  if (typeof document !== 'undefined') {
    const fromCookie = readInstallCookie(INSTALL_DC_COOKIE)
    if (fromCookie === 'ovh-ca' || fromCookie === 'aws-ca' || fromCookie === 'do-sfo') {
      setSelectedDC(fromCookie)
    }
  }
}

/** DC-specific GitHub App install URL bound to the active VeilStream org. */
export async function getGitHubAppInstallUrl(returnPath = '/projects'): Promise<string> {
  const path = normalizeInstallReturnPath(returnPath)
  primeGitHubAppInstallReturn(path)
  const data = await apiClient.get<{ state: string }>('/api/github/install-url')
  const url = new URL(getGithubConnectUrl())
  url.searchParams.set('state', data.state)
  return url.toString()
}

/** True when the URL (or GitHub referrer) indicates a fresh install return — not sessionStorage alone. */
export function hasActiveInstallCallback(searchParams: URLSearchParams): boolean {
  let installationId = searchParams.get('installation_id')
  let setupAction = searchParams.get('setup_action')

  if (!installationId && typeof document !== 'undefined' && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      if (referrerUrl.hostname === 'github.com' && referrerUrl.pathname.includes('/settings/installations/')) {
        const match = referrerUrl.pathname.match(/\/settings\/installations\/(\d+)/)
        if (match?.[1]) {
          installationId = match[1]
          setupAction = 'install'
        }
      }
    } catch {
      // ignore invalid referrer
    }
  }

  return Boolean(
    isGitHubInstallationId(installationId) &&
      (!setupAction || setupAction === 'install' || setupAction === 'update'),
  )
}

/** Read installation callback params from the URL, referrer, or pending session retry. */
export function detectGitHubInstallationCallback(searchParams: URLSearchParams): {
  installationId: string
  setupAction: string
  state: string
} | null {
  // Origin callbacks reuse installation_id (i_…) and also send installation_receipt.
  if (searchParams.get('installation_receipt') || searchParams.get('installationReceipt')) {
    return null
  }

  let installationId = searchParams.get('installation_id')
  let setupAction = searchParams.get('setup_action')
  const urlState = searchParams.get('state') || ''

  if (!installationId && typeof document !== 'undefined' && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      if (referrerUrl.hostname === 'github.com' && referrerUrl.pathname.includes('/settings/installations/')) {
        const match = referrerUrl.pathname.match(/\/settings\/installations\/(\d+)/)
        if (match?.[1]) {
          installationId = match[1]
          setupAction = 'install'
        }
      }
    } catch {
      // ignore invalid referrer
    }
  }

  if (
    isGitHubInstallationId(installationId) &&
    (!setupAction || setupAction === 'install' || setupAction === 'update')
  ) {
    rememberPendingGitHubInstallation(installationId, urlState)
    return {
      installationId,
      setupAction: setupAction || 'install',
      state: isGitHubInstallState(urlState) ? urlState : peekPendingGitHubInstallState() || '',
    }
  }

  const pending = peekPendingGitHubInstallation()
  if (isGitHubInstallationId(pending)) {
    return { installationId: pending, setupAction: 'install', state: peekPendingGitHubInstallState() || '' }
  }
  if (pending) {
    clearPendingGitHubInstallation()
  }

  return null
}

function formatConnectError(message: string): string {
  if (message.includes('app ID and private key')) {
    return (
      'This GitHub installation belongs to a different data center. ' +
      'Select the region where you installed the app (e.g. San Francisco for veilstream-sfo), then try Connect GitHub again.'
    )
  }
  return message
}

export async function connectGitHubInstallation(installationId: string, state?: string): Promise<void> {
  const params = new URLSearchParams({ installation_id: installationId })
  const signed = state && isGitHubInstallState(state) ? state : peekPendingGitHubInstallState()
  if (signed) {
    params.set('state', signed)
  }
  try {
    await apiClient.post(`/api/github/installations/connect?${params.toString()}`, {})
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Failed to connect GitHub App installation'
    throw new Error(formatConnectError(message))
  }
}
